import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Heart, Share2, Star, ShieldCheck, Truck, RefreshCcw, Package, Minus, Plus, ChevronRight, MessageCircle, Instagram } from "lucide-react";
import { fetchProduct, fetchRelated, fetchReviews, formatINR, imageUrl } from "../lib/api";
import { useShop } from "../context/ShopContext";
import ProductCard from "../components/ProductCard";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const { addToCart, toggleWishlist, isInWishlist } = useShop();

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setActiveImage(0);
    fetchProduct(slug).then((p) => {
      setProduct(p);
      setVariant(p.variants?.[0] || null);
    }).catch(() => toast.error("Product not found"));
    fetchRelated(slug).then(setRelated).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (product?.id) fetchReviews(product.id).then(setReviews).catch(() => {});
  }, [product?.id]);

  if (!product) return <div className="min-h-[60vh] flex items-center justify-center text-brand-muted">Loading…</div>;

  const discount = product.compare_at_price ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100) : 0;
  const wished = isInWishlist(product.id);

  const buyNow = () => {
    addToCart(product, qty, variant);
    nav("/checkout");
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-brand-muted mb-6">
        <Link to="/" className="hover:text-brand-maroon">Home</Link>
        <ChevronRight size={12} />
        <Link to="/shop" className="hover:text-brand-maroon">Shop</Link>
        <ChevronRight size={12} />
        <span className="text-brand-charcoal">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div>
          <div className="aspect-[4/5] bg-brand-ivory-soft overflow-hidden mb-4">
            <img src={imageUrl(product.images[activeImage])} alt={product.name} className="w-full h-full object-cover" data-testid="product-main-image" />
          </div>
          <div className="grid grid-cols-4 gap-3">
            {product.images.map((img, i) => (
              <button key={i} onClick={() => setActiveImage(i)} className={`aspect-square overflow-hidden border ${activeImage === i ? "border-brand-maroon" : "border-brand-gold/30"}`} data-testid={`product-thumb-${i}`}>
                <img src={imageUrl(img)} alt={`${product.name} view ${i+1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div>
          <p className="label-tiny text-brand-gold">{product.collections?.[0] || "Bindni Signature"}</p>
          <h1 className="font-serif-brand text-4xl lg:text-5xl text-brand-charcoal mt-2 leading-tight" data-testid="product-name">{product.name}</h1>
          <div className="flex items-center gap-3 mt-3">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} fill={i < Math.round(product.rating) ? "#C5A059" : "none"} color="#C5A059" strokeWidth={1} />
              ))}
            </div>
            <span className="text-xs text-brand-muted">{product.rating} · {product.review_count} reviews</span>
          </div>

          <div className="flex items-baseline gap-3 mt-6">
            <span className="text-3xl font-serif-brand text-brand-maroon" data-testid="product-price">{formatINR(product.price)}</span>
            {product.compare_at_price && (
              <>
                <span className="text-brand-muted line-through">{formatINR(product.compare_at_price)}</span>
                <span className="label-tiny bg-brand-gold/20 text-brand-maroon px-2 py-1">Save {discount}%</span>
              </>
            )}
          </div>
          <p className="text-xs text-brand-muted mt-1">Inclusive of all taxes · Free shipping above ₹999</p>

          <p className="text-brand-charcoal/85 leading-relaxed mt-6">{product.description}</p>

          {product.variants?.length > 0 && (
            <div className="mt-8">
              <p className="label-tiny text-brand-charcoal mb-3">Select {product.variants[0].startsWith("US") || product.variants[0].startsWith("Size") ? "Size" : "Variant"}</p>
              <div className="flex gap-2 flex-wrap">
                {product.variants.map((v) => (
                  <button
                    key={v} onClick={() => setVariant(v)}
                    className={`px-4 py-2 text-sm border transition-colors duration-200 ${variant === v ? "bg-brand-maroon text-brand-ivory border-brand-maroon" : "border-brand-gold/50 hover:border-brand-maroon"}`}
                    data-testid={`variant-${v.replace(/\s+/g, '-')}`}
                  >{v}</button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 mt-8">
            <div className="flex items-center border border-brand-gold/40">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-3 hover:bg-brand-ivory-soft" data-testid="qty-decrease"><Minus size={14} /></button>
              <span className="px-5 text-sm font-medium" data-testid="qty-display">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-3 py-3 hover:bg-brand-ivory-soft" data-testid="qty-increase"><Plus size={14} /></button>
            </div>
            <button onClick={() => addToCart(product, qty, variant)} className="btn-primary flex-1" data-testid="add-to-cart-btn">Add to Cart</button>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <button onClick={buyNow} className="btn-outline-gold flex-1" data-testid="buy-now-btn">Buy It Now</button>
            <button onClick={() => toggleWishlist(product)} className="p-3.5 border border-brand-gold/40 hover:border-brand-maroon" data-testid="detail-wishlist-btn" aria-label="Wishlist">
              <Heart size={18} strokeWidth={1.5} fill={wished ? "#6B111A" : "none"} color="#6B111A" />
            </button>
            <button onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); }} className="p-3.5 border border-brand-gold/40 hover:border-brand-maroon" aria-label="Share" data-testid="share-btn">
              <Share2 size={18} strokeWidth={1.5} />
            </button>
          </div>

          <p className="text-xs text-brand-muted mt-4">Estimated delivery: <span className="text-brand-charcoal font-medium">3-7 business days · PAN India</span></p>

          {/* Shop on WhatsApp / Instagram */}
          <div className="flex gap-3 mt-5" data-testid="product-social-shop">
            <a href={`https://wa.me/c/917987780925?text=${encodeURIComponent("Hi! I'd like to know more about " + product.name)}`} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#25D366] text-white text-xs tracking-[0.2em] uppercase font-medium hover:brightness-110" data-testid="pdp-shop-whatsapp"><MessageCircle size={14} strokeWidth={1.8} /> Shop on WhatsApp</a>
            <a href="https://www.instagram.com/bindni_jewels" target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-2 py-3 text-white text-xs tracking-[0.2em] uppercase font-medium hover:brightness-110" style={{ background: "linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4)" }} data-testid="pdp-shop-instagram"><Instagram size={14} strokeWidth={1.8} /> Shop on Instagram</a>
          </div>

          {/* Trust badges */}
          <div className="grid grid-cols-2 gap-3 mt-8 pt-8 border-t border-brand-gold/25">
            {[
              { icon: ShieldCheck, l: "Anti-Tarnish Guarantee" },
              { icon: Truck, l: "Free Shipping ₹999+" },
              { icon: RefreshCcw, l: "15-Day Easy Returns" },
              { icon: Package, l: "Premium Gift Box" },
            ].map((b) => (
              <div key={b.l} className="flex items-center gap-2 text-xs text-brand-charcoal">
                <b.icon size={16} strokeWidth={1.4} className="text-brand-maroon" /> {b.l}
              </div>
            ))}
          </div>

          {/* Highlights */}
          <div className="mt-10 space-y-3 text-sm">
            <div className="grid grid-cols-3 gap-3 py-2 border-b border-brand-gold/20"><span className="label-tiny text-brand-muted">Material</span><span className="col-span-2 text-brand-charcoal">{product.material}</span></div>
            {product.dimensions && <div className="grid grid-cols-3 gap-3 py-2 border-b border-brand-gold/20"><span className="label-tiny text-brand-muted">Dimensions</span><span className="col-span-2 text-brand-charcoal">{product.dimensions}</span></div>}
            <div className="grid grid-cols-3 gap-3 py-2 border-b border-brand-gold/20"><span className="label-tiny text-brand-muted">Care</span><span className="col-span-2 text-brand-charcoal">{product.care_instructions}</span></div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-24" data-testid="product-reviews">
        <p className="label-tiny text-brand-gold">Customer Reviews</p>
        <h2 className="font-serif-brand text-3xl lg:text-4xl text-brand-charcoal mt-2 mb-8">Loved by our Bindnis</h2>
        {reviews.length === 0 ? (
          <p className="text-sm text-brand-muted">No reviews yet. Be the first to review this piece.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((r) => (
              <div key={r.id} className="border border-brand-gold/25 p-6 bg-brand-ivory-soft">
                {r.image_url && <img src={r.image_url} alt="" className="w-full h-48 object-cover mb-4" />}
                <div className="flex gap-0.5 mb-2">
                  {[...Array(r.rating)].map((_, i) => <Star key={i} size={12} fill="#C5A059" strokeWidth={0} />)}
                </div>
                <p className="font-serif-brand text-lg text-brand-charcoal">{r.title}</p>
                <p className="text-sm text-brand-muted mt-2">{r.body}</p>
                <p className="label-tiny text-brand-maroon mt-4">— {r.customer_name}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-24" data-testid="related-products">
          <p className="label-tiny text-brand-gold">You may also love</p>
          <h2 className="font-serif-brand text-3xl lg:text-4xl text-brand-charcoal mt-2 mb-8">Frequently Bought Together</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 lg:gap-x-8 gap-y-12">
            {related.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
